import { apiFetch, apiGet, apiSend } from '../../session/client';
import { type Consultation, getListings, getOrdersById, listConsultations } from '../bookings/bookings-api';
import { type Order, getHandover } from '../orders/orders-api';
import { getDirectory } from '../places/places-api';

/**
 * Everything the Prescriptions page reads and writes, and nothing it invents.
 *
 * - `consumer/consultations` and the orders they were paid on — the person's
 *   private prescriptions (the same routes the Bookings page uses).
 * - `documents/uploads` + `documents/:id/complete` — a photo or PDF of a
 *   prescription, kept as the person's own PRESCRIPTION document.
 * - `consumer/prescription-requests` — what the person has asked a pharmacy
 *   for: a repeat, or the supply of an uploaded prescription (stage 10, D10
 *   phase 1). The pharmacy works it from its dashboard.
 * - `consumer/orders/:id/handover` — the collection code of a ready order.
 *
 * There is no person id in any path or body except the one the Documents
 * service requires to name the uploader as owner, which it checks against the
 * session.
 */

export type RequestStatus =
  'REQUESTED' | 'WITH_GP' | 'CHECKING' | 'DISPENSING' | 'READY' | 'COMPLETED' | 'DECLINED' | 'CANCELLED';

export type UploadType = 'PRIVATE' | 'NHS_PAPER' | 'VETERINARY';

export interface PrescriptionRequest {
  readonly id: string;
  readonly reference: string;
  readonly kind: 'REPEAT' | 'UPLOAD';
  readonly uploadType?: UploadType;
  readonly documentId?: string;
  readonly documentName?: string;
  readonly items: readonly { readonly name: string; readonly quantity?: string }[];
  readonly gpPractice?: string;
  readonly note?: string;
  readonly storefront: { readonly id: string; readonly name: string };
  readonly status: RequestStatus;
  readonly history: readonly {
    readonly status: RequestStatus;
    readonly at: string;
    readonly by: 'PATIENT' | 'PHARMACY';
    readonly message?: string;
  }[];
  readonly declineReason?: string;
  readonly collectionCode?: string;
  readonly canCancel: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface NewRequest {
  readonly kind: 'REPEAT' | 'UPLOAD';
  readonly storefrontId: string;
  readonly uploadType?: UploadType;
  readonly documentId?: string;
  readonly items?: readonly { readonly name: string; readonly quantity?: string }[];
  readonly gpPractice?: string;
  readonly contactPhone?: string;
  readonly note?: string;
}

/** A pharmacy the person can send a request to. */
export interface PharmacyChoice {
  readonly id: string;
  readonly name: string;
  readonly place?: string;
}

const id = (value: string): string => encodeURIComponent(value);

export const listRequests = (): Promise<readonly PrescriptionRequest[]> =>
  apiGet<{ requests: PrescriptionRequest[] }>('/consumer/prescription-requests').then(
    (found) => found.requests,
  );

export const getRequest = (requestId: string): Promise<PrescriptionRequest> =>
  apiGet<PrescriptionRequest>(`/consumer/prescription-requests/${id(requestId)}`);

export const sendRequest = (request: NewRequest): Promise<PrescriptionRequest> =>
  apiSend<PrescriptionRequest>('POST', '/consumer/prescription-requests', request);

export const cancelRequest = (requestId: string): Promise<PrescriptionRequest> =>
  apiSend<PrescriptionRequest>('POST', `/consumer/prescription-requests/${id(requestId)}/cancel`);

/**
 * A prescription photo or PDF, through the platform's own Documents routes: the
 * record first, then the bytes. Owned by the person uploading it — Documents
 * refuses any other owner.
 */
export async function uploadPrescription(
  personId: string,
  filename: string,
  dataBase64: string,
): Promise<{ id: string }> {
  const begun = await apiSend<{ id: string }>('POST', '/documents/uploads', {
    purpose: 'PRESCRIPTION',
    filename,
    ownerPersonId: personId,
  });
  await apiSend<{ id: string }>('POST', `/documents/${id(begun.id)}/complete`, { dataBase64 });
  return { id: begun.id };
}

/** The person's own uploaded file, authorised on every request. Undefined when it cannot be read. */
export async function downloadDocument(documentId: string): Promise<Blob | undefined> {
  const response = await apiFetch(`/documents/${id(documentId)}/download`);
  return response.ok ? response.blob() : undefined;
}

export { getHandover, getOrdersById, listConsultations };

/** The storefront name and place of each consultation's listing, read once each. */
export async function consultationShops(
  consultations: readonly Consultation[],
): Promise<ReadonlyMap<string, { readonly name: string; readonly slug: string; readonly place?: string }>> {
  const listings = await getListings(consultations.map((c) => c.listingId));
  const shops = new Map<string, { name: string; slug: string; place?: string }>();
  for (const listing of listings.values()) {
    const where = listing.locations[0];
    const place = [where?.town, where?.postcode].filter(Boolean).join(', ');
    shops.set(listing.storefront.id, {
      name: listing.storefront.name,
      slug: listing.storefront.slug,
      ...(place !== '' ? { place } : {}),
    });
  }
  return shops;
}

/** Pharmacies near a postcode, from the storefront directory (Places near you). */
export async function pharmaciesNear(postcode: string): Promise<readonly PharmacyChoice[]> {
  const page = await getDirectory({ postcode, limit: 10 });
  return page.places.map((place) => {
    const where = [place.town, place.postcode].filter(Boolean).join(', ');
    return { id: place.id, name: place.name, ...(where !== '' ? { place: where } : {}) };
  });
}

export type { Consultation, Order };
